package org.catharsis.spendtel.feature.presentation

import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.navigation.NavHostController
import androidx.navigation.compose.currentBackStackEntryAsState
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.core.navigation.ExpenseReportRoute
import org.catharsis.spendtel.core.navigation.showsWebLandingPage
import org.koin.compose.viewmodel.koinViewModel

class HomeScreenBindings(
    val state: HomeState,
    val snackbarHostState: SnackbarHostState,
    val accountMenuExpanded: Boolean,
    val onAccountMenuToggle: () -> Unit,
    val onAccountMenuDismiss: () -> Unit,
    val onSignOut: () -> Unit,
    val onOpenSettings: () -> Unit,
    val onRefresh: () -> Unit,
    val onSelectStatements: () -> Unit,
    val onOpenReport: (String) -> Unit,
    val onDeleteReport: (String) -> Unit,
    val onDismissPaywall: () -> Unit,
    val onPurchaseReport: () -> Unit,
)

@Composable
fun rememberHomeScreenBindings(
    navHostController: NavHostController,
    homeViewModel: HomeViewModel = koinViewModel(),
): HomeScreenBindings {
    val state by homeViewModel.state.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }
    var accountMenuExpanded by remember { mutableStateOf(false) }
    val currentBackStackEntry by navHostController.currentBackStackEntryAsState()
    var previousRoute by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(currentBackStackEntry) {
        val currentRoute = currentBackStackEntry?.destination?.route
        if (
            currentRoute == AppRoutes.Home.name &&
            previousRoute != null &&
            previousRoute != AppRoutes.Home.name
        ) {
            homeViewModel.postEvent(HomeEvent.OnRefresh)
        }
        previousRoute = currentRoute
    }

    LaunchedEffect(Unit) {
        homeViewModel.effect.collect { effect ->
            when (effect) {
                is HomeEffect.OnExpenseReportCreated -> {
                    val count = effect.response.sourceDocuments?.size ?: 0
                    val label = if (count == 1) "1 statement" else "$count statements"
                    snackbarHostState.showSnackbar(
                        "Report started for $label. We're analyzing your spending now.",
                    )
                }
                is HomeEffect.NavigateToExpenseReport -> {
                    navHostController.navigate(ExpenseReportRoute(effect.reportId))
                }
                is HomeEffect.OnError -> {
                    snackbarHostState.showSnackbar(effect.message)
                }
                HomeEffect.OnReportDeleted -> {
                    snackbarHostState.showSnackbar("Report deleted.")
                }
                HomeEffect.OnSignedOut -> {
                    val destination = if (showsWebLandingPage()) {
                        AppRoutes.Landing.name
                    } else {
                        AppRoutes.Authentication.name
                    }
                    navHostController.navigate(destination) {
                        popUpTo(AppRoutes.Home.name) { inclusive = true }
                    }
                }
                is HomeEffect.OnPurchaseComplete -> {
                    snackbarHostState.showSnackbar(effect.message)
                }
                HomeEffect.OnPurchaseProcessing -> {
                    snackbarHostState.showSnackbar("Confirming your payment…")
                }
                is HomeEffect.OnResumingAfterPurchase -> {
                    val label = if (effect.statementCount == 1) {
                        "1 statement"
                    } else {
                        "${effect.statementCount} statements"
                    }
                    snackbarHostState.showSnackbar(
                        "Payment received! Creating your spending story from $label.",
                    )
                }
            }
        }
    }

    return HomeScreenBindings(
        state = state,
        snackbarHostState = snackbarHostState,
        accountMenuExpanded = accountMenuExpanded,
        onAccountMenuToggle = { accountMenuExpanded = !accountMenuExpanded },
        onAccountMenuDismiss = { accountMenuExpanded = false },
        onSignOut = {
            accountMenuExpanded = false
            homeViewModel.postEvent(HomeEvent.OnSignOut)
        },
        onOpenSettings = {
            accountMenuExpanded = false
            navHostController.navigate(AppRoutes.Settings.name)
        },
        onRefresh = { homeViewModel.postEvent(HomeEvent.OnRefresh) },
        onSelectStatements = { homeViewModel.postEvent(HomeEvent.OnSelectStatements) },
        onOpenReport = { reportId ->
            navHostController.navigate(ExpenseReportRoute(reportId))
        },
        onDeleteReport = { reportId ->
            homeViewModel.postEvent(HomeEvent.OnDeleteReport(reportId))
        },
        onDismissPaywall = { homeViewModel.postEvent(HomeEvent.OnDismissPaywall) },
        onPurchaseReport = { homeViewModel.postEvent(HomeEvent.OnPurchaseReport) },
    )
}
